import React, { useCallback, useEffect, useMemo } from 'react';
import { number, array, func, string, object, bool } from 'prop-types';
import { debounce } from 'lodash';
import styled from 'styled-components';
import CheckboxTitle from 'apps/journey/components/checkbox/checkbox-title';
import { searchReportOutcomes } from 'apps/journey/pages/professional-center/queries/searchReportOutcomes';
import { withA11y, withData } from 'util/hoc';
import {
  SearchIcon,
  StyledSearchContainer,
  StyledSearchInnerContainer,
} from 'apps/journey/pages/career-exploration/components/filters/styled-filter-controls';
import Input from 'library/components/form-components/input';
import PaginationControls from 'library/components/search-table/pagination-controls';
import { StyledPaginationControls } from 'library/components/search-table/styled-pagination-controls';
import { resolve } from 'pairin-library/helpers/path-resolver';

const StyledOutcomesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 1.5em;

  ${StyledSearchInnerContainer} {
    width: 100%;
  }

  ${StyledSearchContainer} {
    width: 100%;
    margin: 1em 0;
  }

  ${StyledPaginationControls} {
    margin: 1em 0;
    align-self: flex-start;
  }
`;

const GenerateReportOutcomeData = ({
  outcomesChecked,
  handlePage,
  handleSearch,
  currentPage,
  searchInput,
  handleChecked,
  isTabbing,
  graph_data,
  graph_loading,
  reload_graph,
}) => {
  useEffect(() => {
    reload_graph();
  }, [currentPage, reload_graph]);
  useEffect(() => {
    reload_graph();
  }, [searchInput, reload_graph]);
  const checkBoxList = useMemo(() => {
    const responseData = resolve(graph_data, 'outcomes_for_report/results', []);
    const outcomeData = [{ id: 0, name: 'Outcome Data' }, ...responseData];
    const handleCheckedAction = ({ name }) => {
      if (!name) {
        handleChecked(
          name,
          outcomeData.map((x) => x.id)
        );
      } else {
        handleChecked(name);
      }
    };
    return outcomeData.map(({ name, id }, index) => (
      <CheckboxTitle
        key={`${currentPage}_${id}`}
        tabIndex={0}
        isTabbing={isTabbing}
        name={id}
        isChecked={outcomesChecked?.some((el) => el === id)}
        handleChange={handleCheckedAction}
      >
        {name}
        {index === 0 &&
          ` (${outcomesChecked.filter((x) => x !== 0).length} Selected)`}
      </CheckboxTitle>
    ));
  }, [graph_data, currentPage, handleChecked, isTabbing, outcomesChecked]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(handleSearch, 1000), []);

  if (graph_loading) return null;
  const pagination = resolve(graph_data, 'outcomes_for_report/pagination', {});

  return (
    <StyledOutcomesWrapper>
      <StyledSearchContainer>
        <StyledSearchInnerContainer>
          <Input
            name="searchValue"
            label="Search Outcomes"
            type="text"
            value={searchInput}
            onChange={debouncedSearch}
            focus
          />
          <SearchIcon
            aria-label="Close search"
            tabIndex={0}
            isTabbing={isTabbing}
          />
        </StyledSearchInnerContainer>
      </StyledSearchContainer>
      {checkBoxList}
      <PaginationControls
        handleGoToPage={handlePage}
        pageSize={5}
        totalCount={pagination.total}
      />
    </StyledOutcomesWrapper>
  );
};

GenerateReportOutcomeData.query = async (
  token,
  { searchInput, currentPage }
) => {
  const offset = (currentPage > 0 ? currentPage - 1 : 0) * 5;
  return await searchReportOutcomes(searchInput, offset, 5);
};

GenerateReportOutcomeData.propTypes = {
  outcomesChecked: array,
  handlePage: func,
  handleSearch: func,
  currentPage: number,
  searchInput: string,
  handleChecked: func,
  isTabbing: bool,
  graph_data: object,
  graph_loading: bool,
  reload_graph: func,
};

GenerateReportOutcomeData.defaultProps = {
  outcomesChecked: [],
  currentPage: 1,
  searchInput: '',
};

export default withA11y(withData(GenerateReportOutcomeData));
